import React, { useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Amount from '@components/lpc/Dasboard/PotsSection/Card/ActivePot/Amount/Amount';
import BeneficiaryMobileBloc from '@components/lpc/Pot/AmountModule/BeneficiaryMobileBloc/BeneficiaryMobileBloc';
import ParticipantMobileBloc from '@components/lpc/Pot/AmountModule/ParticipantMobileBloc/ParticipantMobileBloc';
import Participate from '@components/lpc/Pot/AmountModule/Participate/Participate';
import CancelOffer from '@components/lpc/Pot/MainModule/History/Offer/CancelOffer/CancelOffer';
import SharePot from '@components/lpc/Pot/SharePot/SharePot';
import { personRoles } from '@models/PotModel/personRoles';
import { statusPO } from '@models/PotModel/statusPO';
import { potType } from '@propTypes/potTypes';
import { getRoute, ROUTE } from '@services/http/Route';

import css from './AmountModule.module.scss';

type AmountModuleProps = {
    pot: potType;
    personRole: string;
    potStatus: string;
    onGoToParticipantsList: () => void;
    setOpenParticipate: (boolean) => void;
};

const AmountModule = ({ pot, personRole, potStatus, setOpenParticipate }: AmountModuleProps) => {
    const { t } = useTranslateLpc(['lpc-create-pot', 'common', 'lpc-pot']);
    const router = useRouter();
    const participateRef = useRef<any>(null);
    const [showParticipantsBloc, setShowParticipantsBloc] = useState<boolean>(false);

    const refreshData = () => {
        router.replace(router.asPath);
    };
    const handleScroll = () => {
        if (participateRef.current) {
            const buttonPosition = participateRef.current.getBoundingClientRect().top;
            if (buttonPosition <= 0) {
                setShowParticipantsBloc(true);
            } else {
                setShowParticipantsBloc(false);
            }
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <>
            <section className={css.top}>
                <Amount
                    hideCollectedAmount={!pot.privacy.showTotalContribution && personRole === personRoles.PARTICIPANT}
                    amountLeft={pot.amountLeft}
                    collectorAmountLeft={pot.collectorAmountLeft}
                    uniq={pot.uniq}
                    personRole={personRole}
                    isPotPage
                    spentAmount={pot.spentAmount}
                    collectedAmount={pot.collectedAmount}
                    createdOn={pot.creationDate}
                    amountToReach={pot.poolObjective.amountToReach}
                    dateLimit={pot.poolObjective.dateLimit}
                />
            </section>

            {personRole === personRoles.BENEFICIARY && <BeneficiaryMobileBloc pot={pot} />}
            {personRole === personRoles.PARTICIPANT && showParticipantsBloc && (
                <ParticipantMobileBloc pot={pot} setOpenParticipate={(value) => setOpenParticipate(value)} />
            )}
            <div className={css.middle_bottom_container}>
                {(potStatus === statusPO.CLOSED && personRole !== personRoles.BENEFICIARY) ||
                    (personRole === personRoles.ORGANISER && !pot.isOffered && (
                        <section
                            className={
                                personRole === personRoles.ORGANISER
                                    ? `${css.bottom} ${css.middle_bottom_container__custom_bottom}`
                                    : `${css.bottom}`
                            }
                        >
                            {potStatus === statusPO.CLOSED && personRole !== personRoles.BENEFICIARY && (
                                <div className={css.bottom__closed_container}>
                                    <Button
                                        customClass={css.bottom__closed_container__closed}
                                        variant={'primary_muted'}
                                    >
                                        {pot.isOffered ? t('amount.cta.offered-pot') : t('amount.cta.closed-pot')}
                                    </Button>
                                </div>
                            )}
                            {personRole === personRoles.ORGANISER && !pot.isOffered && (
                                <div className={css.bottom__bottoms_container}>
                                    <div className={css.bottom__bottoms_container__participate}>
                                        <Participate
                                            ref={participateRef}
                                            isFrozen={pot.isFrozenAccount}
                                            potStatus={potStatus}
                                            potRef={pot.uniq}
                                            poolContribution={pot.poolContribution}
                                            personRole={pot.personRole}
                                            commissionType={pot.commissionType}
                                            setOpenParticipate={(value) => {
                                                setOpenParticipate(value);
                                            }}
                                            customVariant={'primary'}
                                            customCta={`${css.bottom__custom_container} ${css.bottom__custom_container__border}`}
                                            customChildren={css.bottom__bottoms_container__participate__children}
                                        >
                                            <img
                                                className={css.bottom__bottoms_container__participate__img}
                                                src="/front-static/icons/action/add-outline-white.svg"
                                                alt="participate"
                                            />
                                        </Participate>
                                    </div>

                                    {pot.personRole === personRoles.ORGANISER && (
                                        <SharePot
                                            namePot={pot.title}
                                            isOffered={pot.isOffered}
                                            personRole={pot.personRole}
                                            potRef={pot.uniq}
                                            potLink={pot.absoluteUrl}
                                        />
                                    )}
                                </div>
                            )}
                        </section>
                    ))}
            </div>
            <section
                className={
                    personRole === personRoles.BENEFICIARY
                        ? `${css.bottom} ${css.bottom__for_beneficiary}`
                        : `${css.bottom}`
                }
            >
                {personRole === personRoles.ORGANISER && pot.isWaitingForOfferConfirmation && (
                    <CancelOffer
                        ctaLabel={t('amount.cta.cancel-offer')}
                        potRef={pot.uniq}
                        potTitle={pot.title}
                        benifFirstName={pot.pendingBeneficiary?.firstName}
                        benifLastName={pot.pendingBeneficiary?.lastName}
                        onUpdate={refreshData}
                        customClass={css.bottom__cancel_offer}
                    />
                )}
                {personRole === personRoles.BENEFICIARY && (
                    <>
                        <div className={css.bottom__beneficiary}>
                            <div className={css.bottom__beneficiary__use}>
                                <Link
                                    href={getRoute(ROUTE.LPC.POT.POT, pot.uniq, ROUTE.LPC.POT.SPENDING)}
                                    legacyBehavior
                                >
                                    <Button
                                        customClass={css.bottom__bottoms_container__spend_100}
                                        isDisabled={pot.isFrozenAccount}
                                        variant={'primary'}
                                    >
                                        <div className={css.bottom__bottoms_container__participate__children}>
                                            {t('amount.cta.spend-pot')}
                                        </div>
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </>
                )}
                {personRole === personRoles.PARTICIPANT && potStatus !== statusPO.CLOSED && (
                    <div className={css.bottom__bottoms_container}>
                        <div className={css.bottom__bottoms_container__participate}>
                            <Participate
                                ref={participateRef}
                                isFrozen={pot.isFrozenAccount}
                                potRef={pot.uniq}
                                potStatus={potStatus}
                                poolContribution={pot.poolContribution}
                                personRole={pot.personRole}
                                commissionType={pot.commissionType}
                                setOpenParticipate={(value) => {
                                    setOpenParticipate(value);
                                }}
                                customCta={css.bottom__custom_container}
                                customChildren={css.bottom__bottoms_container__participate__children}
                            >
                                <img
                                    className={css.bottom__bottoms_container__participate__img}
                                    src="/front-static/icons/action/add-outline-white.svg"
                                    alt="participate"
                                />
                            </Participate>
                        </div>
                        <SharePot
                            namePot={pot.title}
                            isOffered={pot.isOffered}
                            personRole={pot.personRole}
                            potRef={pot.uniq}
                            potLink={pot.absoluteUrl}
                        />
                    </div>
                )}
            </section>
        </>
    );
};

export default AmountModule;
